import React, { useRef, useState, useEffect, useCallback } from "react";
import { Row, Col, Button, Card, CardBody, CardFooter } from "reactstrap";
import AdvanceTableWrapper from "../../advanceTable/AdvanceTableWrapper";
import AdvanceTable from "../../advanceTable/AdvanceTable";
import AdvanceTablePagination from "../../advanceTable/AdvanceTablePagination";
import { useJobDetails } from "../../../providers/jobDetailsProvider";
import { jobsApi } from "../../../services/jobServices";
import { utils } from "../../../utils/utils";
import ConfirmationModal from "../../ConfirmationModal";
import InformationModal from "../../InformationModal";

const columns = [
  {
    accessor: "pageNumber",
    Header: "Page #",
    headerProps: { className: "text-truncate" },
    width: 120,
  },
  {
    accessor: "itemNumber",
    Header: "Item #",
    headerProps: { className: "text-truncate" },
    width: 60,
  },
  {
    accessor: "qty",
    Header: "Qty",
    headerProps: { className: "text-truncate" },
    width: 50,
  },
  {
    accessor: "sh",
    Header: "Sh",
    headerProps: { className: "text-truncate" },
    width: 50,
  },
  {
    accessor: "dimensions",
    Header: "Dimensions",
    headerProps: { className: "text-truncate" },
    width: 80,
  },
  {
    accessor: "length",
    Header: "Length",
    headerProps: { className: "text-truncate" },
    width: 120,
  },
  {
    accessor: "grade",
    Header: "Grade",
    headerProps: { className: "text-truncate" },
    width: 70,
  },
  {
    accessor: "unitWeight",
    Header: "Unit Weight",
    headerProps: { className: "text-truncate" },
    width: 80,
  },
  {
    accessor: "totalWeight",
    Header: "Total Weight",
    headerProps: { className: "text-truncate" },
    width: 120,
  },
  {
    accessor: "fin",
    Header: "Fin",
    headerProps: { className: "text-truncate" },
    width: 70,
  },
  {
    accessor: "cat",
    Header: "Cat",
    headerProps: { className: "text-truncate" },
    width: 50,
  },
  {
    accessor: "sub",
    Header: "Sub",
    headerProps: { className: "text-truncate" },
    width: 50,
  },
  {
    accessor: "sa",
    Header: "SA",
    headerProps: { className: "text-truncate" },
    width: 50,
  },
  {
    accessor: "comment",
    Header: "Comment",
    headerProps: { className: "text-truncate" },
    width: 120,
  },
  {
    accessor: "misc",
    Header: "Misc",
    headerProps: { className: "text-truncate ellipsis" },
    width: 60,
  },
];

const TakeOffTab = () => {
  const [jobDetails] = useJobDetails();
  const fileInputRef = useRef(null);
  const [takeOffs, setTakeOffs] = useState({
    data: [],
    count: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [confirmationModal, setConfirmationModal] = useState(
    utils.initConfirmationModal
  );
  const [informationModal, setInformationModal] = useState(
    utils.initInformationModal
  );

  const fetchTakeOffs = useCallback(async () => {
    if (jobDetails.job?.id) {
      setLoading(true);
      try {
        const data = await jobsApi.getTakeOffs(jobDetails.job.id, {
          page: page - 1,
          pageSize: sizePerPage,
        });
        setTakeOffs(data);
      } catch (error) {
        setInformationModal({
          isOpen: true,
          title: "Error",
          body: "An error occurred while fetching the takeoff items.",
        });
      } finally {
        setLoading(false);
      }
    }
  }, [jobDetails.job?.id, page, sizePerPage]);

  useEffect(() => {
    fetchTakeOffs();
  }, [fetchTakeOffs]);

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setConfirmationModal({
        isOpen: true,
        title: "Upload Takeoff",
        body: `Are you sure you want to upload the file: ${file.name}?`,
        onSubmit: async () => {
          const formData = new FormData();
          formData.append("file", file);
          setUploading(true);
          try {
            await jobsApi.uploadTakeoff(jobDetails.job.id, formData);
            fetchTakeOffs();
          } catch (error) {
            setInformationModal({
              isOpen: true,
              title: "Error",
              body: `An error occurred while uploading the file ${file.name}.`,
            });
          } finally {
            setUploading(false);
            setConfirmationModal(utils.initConfirmationModal);
            if (fileInputRef.current) {
              fileInputRef.current.value = null;
            }
          }
        },
        onClose: () => {
          setConfirmationModal(utils.initConfirmationModal);
          if (fileInputRef.current) {
            fileInputRef.current.value = null;
          }
        },
      });
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        accept=".csv"
      />
      <Row className="d-flex justify-content-between px-3 align-items-center">
        <Col className="mt-3 col-12">
          <div className="d-flex align-items-start justify-content-between">
            <h3>Take-Off</h3>
            <Button
              className="rounded"
              size="sm"
              color="success"
              onClick={handleUploadClick}
              disabled={uploading}
            >
              Upload Takeoff
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col className="flex-shrink-0 py-3">
          <Card className="col-12 px-0 overflow-x-auto border-0">
            <CardBody className="border-0">
              <AdvanceTableWrapper
                columns={columns}
                data={takeOffs?.data || []}
                pageSize={sizePerPage}
                // onSort={onSort}
              >
                <AdvanceTable
                  // onRowClick={(row) => details(row.original.id)}
                  table
                  isLoading={loading}
                  headerClassName="text-muted small"
                  tableProps={{
                    striped: true,
                    className: "mb-0",
                  }}
                />
              </AdvanceTableWrapper>
            </CardBody>
            <CardFooter>
              <AdvanceTablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                totalCount={takeOffs.count}
                pageCount={takeOffs.totalPages}
                currentPage={page - 1}
                onPageChange={(newPage) => setPage(newPage)}
                pageSize={sizePerPage}
                onPageSizeChange={(newSizePerPage) => {
                  setSizePerPage(newSizePerPage);
                  setPage(1);
                }}
              />
            </CardFooter>
          </Card>
        </Col>
      </Row>
      {confirmationModal.isOpen && <ConfirmationModal {...confirmationModal} />}
      {informationModal.isOpen && (
        <InformationModal
          {...informationModal}
          onClose={() => setInformationModal(utils.initInformationModal)}
        />
      )}
    </>
  );
};

export default TakeOffTab;
